import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Calendar,
  Clock,
  Timer,
  CreditCard,
  ArrowRight,
  Check,
  ChevronLeft,
  ChevronRight,
  Video,
  Globe,
} from 'lucide-react';
import TimezoneSelect, { detectUserTimezone } from '../TimezoneSelect';
import BookingService from './BookingService';

const ImprovedBookingPage = () => {
  const { t, i18n } = useTranslation();
  const locale = (i18n.language || 'en').split('-')[0];
  const [currentStep, setCurrentStep] = useState(1);
  const [visitorTz, setVisitorTz] = useState(
    () => localStorage.getItem('visitor_timezone') || detectUserTimezone()
  );
  const [bookingData, setBookingData] = useState({
    date: null,
    time: null,
    starts_at: null,
    duration: 30,
    name: '',
    email: '',
    phone: '',
    concerns: '',
  });

  const [prices, setPrices] = useState({ 30: 50, 60: 100 });
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');

  const [availableDates, setAvailableDates] = useState([]);
  const [availableTimesMap, setAvailableTimesMap] = useState({});

  useEffect(() => {
    BookingService.getConsultations().then((items) => {
      if (!items || !items.length) return;
      const next = { 30: 50, 60: 100 };
      items.forEach((p) => {
        if (p.duration_minutes) {
          next[p.duration_minutes] = Number((p.price_cents / 100).toFixed(2));
        }
      });
      setPrices(next);
    });
  }, []);

  useEffect(() => {
    localStorage.setItem('visitor_timezone', visitorTz);
    BookingService.getAvailableSlots(visitorTz, bookingData.duration).then((data) => {
      const dates = [];
      const timesMap = {};
      data.forEach((slot) => {
        const d = new Date(slot.date + 'T00:00:00');
        dates.push(d);
        const detailsByTime = {};
        (slot.slot_details || []).forEach((det) => {
          detailsByTime[det.time] = det.starts_at;
        });
        timesMap[d.toDateString()] = {
          morning: slot.times.filter((tStr) => parseInt(tStr.split(':')[0], 10) < 12),
          afternoon: slot.times.filter((tStr) => parseInt(tStr.split(':')[0], 10) >= 12),
          detailsByTime,
        };
      });
      setAvailableDates(dates);
      setAvailableTimesMap(timesMap);
    });
  }, [visitorTz, bookingData.duration]);

  const handleDateSelect = (date) => {
    setBookingData({ ...bookingData, date, time: null, starts_at: null });
  };

  const handleTimeSelect = (time) => {
    const dayEntry = bookingData.date ? availableTimesMap[bookingData.date.toDateString()] : null;
    const startsAt = dayEntry?.detailsByTime?.[time] || null;
    setBookingData({ ...bookingData, time, starts_at: startsAt });
  };

  const handleDurationSelect = (duration) => {
    setBookingData({ ...bookingData, duration });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBookingData({ ...bookingData, [name]: value });
  };

  const isDateSelected = (date) => {
    return bookingData.date && date.toDateString() === bookingData.date.toDateString();
  };

  const handleStripePayment = async () => {
    setSubmitting(true);
    setServerError('');
    try {
      const isoDate = new Date(
        bookingData.date.getTime() - bookingData.date.getTimezoneOffset() * 60000
      )
        .toISOString()
        .split('T')[0];

      const response = await BookingService.createBooking({
        name: bookingData.name,
        email: bookingData.email,
        phone: bookingData.phone,
        concerns: bookingData.concerns,
        date: isoDate,
        time: bookingData.time,
        starts_at: bookingData.starts_at,
        client_timezone: visitorTz,
        duration_minutes: bookingData.duration,
      });

      window.location.href = response.stripe_checkout_url;
    } catch (error) {
      setServerError(error.message || 'Failed to initialize payment.');
      setSubmitting(false);
    }
  };

  const formatDate = (date) => {
    try {
      return new Intl.DateTimeFormat(locale, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      }).format(date);
    } catch {
      return date.toDateString();
    }
  };

  const nextStep = () => {
    if (currentStep === 1 && (!bookingData.date || !bookingData.time)) return;
    setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  const nextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const prevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const currentMonthDates = availableDates.filter(
    (date) => date.getMonth() === currentMonth && date.getFullYear() === currentYear
  );

  const getMonthName = () => {
    try {
      return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(
        new Date(currentYear, currentMonth, 1)
      );
    } catch {
      return `${currentMonth + 1}/${currentYear}`;
    }
  };

  const activePrice = prices[bookingData.duration] ?? (bookingData.duration === 30 ? 50 : 100);

  return (
    <div className="max-w-4xl mx-auto px-4 py-10">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {t('booking.pageTitle', 'Book a Consultation')}
          </h1>
          <p className="text-gray-600">
            {t(
              'booking.pageSubtitle',
              'Schedule your personalized nutrition session with Paola Michelle'
            )}
          </p>
        </div>
        <TimezoneSelect
          label={t('booking.yourTimezone', 'Your timezone')}
          value={visitorTz}
          onChange={(tz) => {
            setVisitorTz(tz);
            setBookingData((prev) => ({ ...prev, date: null, time: null, starts_at: null }));
          }}
        />
      </div>

      {/* Progress Steps */}
      <div className="relative mb-12">
        <div className="flex justify-between">
          {[1, 2, 3].map((step) => (
            <div
              key={step}
              className={`relative flex items-center justify-center z-10 w-10 h-10 rounded-full ${
                currentStep >= step ? 'bg-primary text-white' : 'bg-gray-200 text-gray-500'
              }`}
            >
              {step}
            </div>
          ))}
        </div>
        <div className="absolute top-5 left-0 w-full h-1 bg-gray-200 -z-10"></div>
        <div
          className="absolute top-5 left-0 h-1 bg-primary -z-5 transition-all duration-500"
          style={{ width: `${(currentStep - 1) * 50}%` }}
        ></div>

        <div className="flex justify-between mt-2">
          <span className="text-sm font-medium">
            {t('booking.step1Label', 'Select Date & Time')}
          </span>
          <span className="text-sm font-medium">
            {t('booking.step2Label', 'Choose Duration')}
          </span>
          <span className="text-sm font-medium">{t('booking.step3Label', 'Your Details')}</span>
        </div>
      </div>

      {/* Step 1: Date and Time Selection */}
      {currentStep === 1 && (
        <div className="bg-white rounded-[2rem] sm:rounded-[2.5rem] shadow-sm border border-gray-100 p-6 sm:p-10 relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-primary/5 rounded-full filter blur-2xl pointer-events-none"></div>

          <div className="flex flex-col lg:flex-row lg:gap-12 relative z-10">
            {/* Left Side: Calendar */}
            <div className="lg:w-1/2 mb-6 lg:mb-0">
              <h2 className="text-xl font-semibold mb-4">
                {t('booking.selectDate', 'Select a Date')}
              </h2>

              <div className="flex items-center justify-between mb-2">
                <button onClick={prevMonth} className="p-1.5 rounded-full hover:bg-gray-100">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <h3 className="text-base font-medium capitalize">{getMonthName()}</h3>
                <button onClick={nextMonth} className="p-1.5 rounded-full hover:bg-gray-100">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              <div className="mb-4">
                <div className="grid grid-cols-7 gap-1 mb-1">
                  {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((day) => (
                    <div
                      key={day}
                      className="text-center text-xs text-gray-400 font-medium uppercase tracking-wider"
                    >
                      {day}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-7 gap-1">
                  {Array.from({
                    length: (new Date(currentYear, currentMonth, 1).getDay() + 6) % 7,
                  }).map((_, index) => (
                    <div key={`empty-start-${index}`} className="h-10 text-xs"></div>
                  ))}

                  {Array.from({
                    length: new Date(currentYear, currentMonth + 1, 0).getDate(),
                  }).map((_, index) => {
                    const day = index + 1;
                    const date = new Date(currentYear, currentMonth, day);
                    const isAvailable = currentMonthDates.some(
                      (availableDate) => availableDate.getDate() === day
                    );

                    return (
                      <button
                        key={`day-${day}`}
                        disabled={!isAvailable}
                        onClick={() => isAvailable && handleDateSelect(date)}
                        className={`w-10 h-10 mx-auto text-sm flex items-center justify-center rounded-full transition-all duration-300 ${
                          isDateSelected(date)
                            ? 'bg-primary text-white shadow-md scale-110 z-10'
                            : isAvailable
                            ? 'hover:bg-primary hover:bg-opacity-10 text-gray-700 cursor-pointer font-semibold'
                            : 'text-gray-300 cursor-not-allowed'
                        }`}
                      >
                        {day}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Right Side: Time Slots */}
            <div className="lg:w-1/2 border-t lg:border-t-0 lg:border-l border-gray-100 pt-6 lg:pt-0 lg:pl-8">
              {bookingData.date ? (
                <div className="animate-in fade-in slide-in-from-right-4 duration-300">
                  <h2 className="text-xl font-semibold mb-1 text-gray-900">
                    {t('booking.availableTimes', 'Available Times')}
                  </h2>
                  <p className="text-sm text-gray-500 mb-6">
                    {formatDate(bookingData.date)} ·{' '}
                    <span className="font-semibold text-gray-700">{visitorTz}</span>
                  </p>

                  <div className="space-y-6">
                    {availableTimesMap[bookingData.date.toDateString()]?.morning?.length > 0 && (
                      <div>
                        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">
                          {t('booking.morning', 'Morning')}
                        </h4>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {availableTimesMap[bookingData.date.toDateString()]?.morning.map(
                            (time) => (
                              <button
                                key={time}
                                onClick={() => handleTimeSelect(time)}
                                className={`py-3 px-4 rounded-full text-sm font-bold text-center transition-all duration-300 ${
                                  bookingData.time === time
                                    ? 'bg-primary text-white shadow-md transform scale-105'
                                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100 hover:-translate-y-1 border border-gray-100 shadow-sm'
                                }`}
                              >
                                {time}
                              </button>
                            )
                          )}
                        </div>
                      </div>
                    )}

                    {availableTimesMap[bookingData.date.toDateString()]?.afternoon?.length > 0 && (
                      <div>
                        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">
                          {t('booking.afternoon', 'Afternoon')}
                        </h4>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {availableTimesMap[bookingData.date.toDateString()]?.afternoon.map(
                            (time) => (
                              <button
                                key={time}
                                onClick={() => handleTimeSelect(time)}
                                className={`py-3 px-4 rounded-full text-sm font-bold text-center transition-all duration-300 ${
                                  bookingData.time === time
                                    ? 'bg-primary text-white shadow-md transform scale-105'
                                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100 hover:-translate-y-1 border border-gray-100 shadow-sm'
                                }`}
                              >
                                {time}
                              </button>
                            )
                          )}
                        </div>
                      </div>
                    )}

                    {!availableTimesMap[bookingData.date.toDateString()]?.morning?.length &&
                      !availableTimesMap[bookingData.date.toDateString()]?.afternoon?.length && (
                        <p className="text-sm text-gray-500 text-center py-4 bg-gray-50 rounded-lg">
                          {t('booking.noSlots', 'No time slots available for this date.')}
                        </p>
                      )}
                  </div>
                </div>
              ) : (
                <div className="h-full flex flex-col items-center justify-center p-8 text-center bg-gray-50 rounded-lg border border-dashed border-gray-200">
                  <Calendar className="w-12 h-12 text-gray-300 mb-4" />
                  <p className="text-gray-500 font-medium">
                    {t('booking.selectDatePrompt', 'Please select a date first')}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end mt-8 pt-4 border-t border-gray-100">
            <button
              onClick={nextStep}
              disabled={!bookingData.date || !bookingData.time}
              className="flex items-center justify-center bg-primary hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white py-3 px-8 rounded-full font-bold transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0"
            >
              {t('booking.continue', 'Continue')} <ArrowRight className="ml-2 w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Duration Selection */}
      {currentStep === 2 && (
        <div className="bg-white rounded-[2rem] sm:rounded-[2.5rem] shadow-sm border border-gray-100 p-6 sm:p-10 relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-blue-400/5 rounded-full filter blur-2xl pointer-events-none"></div>

          <h2 className="text-2xl font-bold mb-8 text-gray-900 relative z-10">
            {t('booking.chooseDuration', 'Choose Consultation Duration')}
          </h2>

          <div className="grid md:grid-cols-2 gap-4 mb-8">
            <div
              onClick={() => handleDurationSelect(30)}
              className={`border rounded-[2rem] p-8 cursor-pointer transition-all duration-300 hover:shadow-md hover:-translate-y-1 bg-white relative z-10 ${
                bookingData.duration === 30
                  ? 'border-primary shadow-sm ring-4 ring-primary/5'
                  : 'border-gray-100'
              }`}
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="font-semibold text-lg">
                    {t('booking.consultation30Title', '30-Minute Consultation')}
                  </h3>
                  <p className="text-gray-500">30 {t('booking.minutes', 'minutes')}</p>
                </div>
                <div
                  className={`w-5 h-5 rounded-full ${
                    bookingData.duration === 30
                      ? 'bg-primary flex items-center justify-center'
                      : 'border border-gray-300'
                  }`}
                >
                  {bookingData.duration === 30 && <Check className="w-3 h-3 text-white" />}
                </div>
              </div>

              <ul className="text-sm text-gray-600 space-y-2">
                <li className="flex items-start">
                  <Check className="w-4 h-4 text-primary mr-2 mt-0.5" />
                  <span>{t('booking.c30Feature1', 'Initial assessment of nutrition needs')}</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-4 h-4 text-primary mr-2 mt-0.5" />
                  <span>{t('booking.c30Feature2', 'Overview of your health concerns')}</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-4 h-4 text-primary mr-2 mt-0.5" />
                  <span>{t('booking.c30Feature3', 'Actionable recommendations')}</span>
                </li>
              </ul>

              <div className="mt-4 text-xl font-bold text-primary">€{prices[30]}</div>
            </div>

            <div
              onClick={() => handleDurationSelect(60)}
              className={`border rounded-[2rem] p-8 cursor-pointer transition-all duration-300 hover:shadow-md hover:-translate-y-1 bg-white relative z-10 ${
                bookingData.duration === 60
                  ? 'border-primary shadow-sm ring-4 ring-primary/5'
                  : 'border-gray-100'
              }`}
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="font-semibold text-lg">
                    {t('booking.consultation60Title', 'Comprehensive Consultation')}
                  </h3>
                  <p className="text-gray-500">60 {t('booking.minutes', 'minutes')}</p>
                </div>
                <div
                  className={`w-5 h-5 rounded-full ${
                    bookingData.duration === 60
                      ? 'bg-primary flex items-center justify-center'
                      : 'border border-gray-300'
                  }`}
                >
                  {bookingData.duration === 60 && <Check className="w-3 h-3 text-white" />}
                </div>
              </div>

              <ul className="text-sm text-gray-600 space-y-2">
                <li className="flex items-start">
                  <Check className="w-4 h-4 text-primary mr-2 mt-0.5" />
                  <span>{t('booking.c60Feature1', 'In-depth nutrition assessment')}</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-4 h-4 text-primary mr-2 mt-0.5" />
                  <span>{t('booking.c60Feature2', 'Detailed discussion of health history')}</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-4 h-4 text-primary mr-2 mt-0.5" />
                  <span>{t('booking.c60Feature3', 'Personalized nutrition strategy')}</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-4 h-4 text-primary mr-2 mt-0.5" />
                  <span>{t('booking.c60Feature4', 'Meal planning guidance')}</span>
                </li>
              </ul>

              <div className="mt-4 text-xl font-bold text-primary">€{prices[60]}</div>
            </div>
          </div>

          <div className="bg-blue-50 p-4 rounded-xl mb-6">
            <h3 className="font-medium text-blue-800 mb-1 flex items-center">
              <Video className="w-4 h-4 mr-2" />
              {t('booking.videoNoticeTitle', 'All consultations take place via secure video call')}
            </h3>
            <p className="text-sm text-blue-700">
              {t(
                'booking.videoNoticeBody',
                "After booking, you'll receive a confirmation email with your video call link and preparation instructions."
              )}
            </p>
          </div>

          <div className="flex justify-between mt-10 pt-6 border-t border-gray-100 relative z-10">
            <button
              onClick={prevStep}
              className="flex items-center justify-center bg-gray-50 hover:bg-gray-100 text-gray-800 py-3 px-8 rounded-full font-bold transition-all"
            >
              <ChevronLeft className="mr-2 w-4 h-4" /> {t('booking.back', 'Go Back')}
            </button>

            <button
              onClick={nextStep}
              className="flex items-center justify-center bg-gray-900 hover:bg-black text-white py-3 px-8 rounded-full font-bold transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5"
            >
              {t('booking.continue', 'Continue')} <ArrowRight className="ml-2 w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Personal Details */}
      {currentStep === 3 && (
        <div className="bg-white rounded-[2rem] sm:rounded-[2.5rem] shadow-sm border border-gray-100 p-6 sm:p-10 relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-green-400/5 rounded-full filter blur-2xl pointer-events-none"></div>

          <h2 className="text-2xl font-bold mb-8 text-gray-900 relative z-10">
            {t('booking.personalInfo', 'Personal Information')}
          </h2>

          <div className="grid md:grid-cols-2 gap-10 mb-6 relative z-10">
            <div>
              <form className="space-y-5" onSubmit={(e) => e.preventDefault()}>
                <div>
                  <label htmlFor="name" className="block text-sm font-bold text-gray-700 mb-2">
                    {t('booking.name', 'Full Name')}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={bookingData.name}
                    onChange={handleInputChange}
                    className="w-full p-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all outline-none"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-bold text-gray-700 mb-2">
                    {t('booking.email', 'Email Address')}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={bookingData.email}
                    onChange={handleInputChange}
                    className="w-full p-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all outline-none"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-bold text-gray-700 mb-2">
                    {t('booking.phone', 'Phone Number')}
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={bookingData.phone}
                    onChange={handleInputChange}
                    className="w-full p-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all outline-none"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="concerns" className="block text-sm font-bold text-gray-700 mb-2">
                    {t('booking.concerns', 'Health Concerns / Notes')}
                  </label>
                  <textarea
                    id="concerns"
                    name="concerns"
                    value={bookingData.concerns}
                    onChange={handleInputChange}
                    rows="4"
                    className="w-full p-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all outline-none"
                  ></textarea>
                </div>
              </form>
            </div>

            <div className="bg-gray-50 p-8 rounded-[2rem] border border-gray-100">
              <h3 className="font-bold text-xl mb-6 text-gray-900 border-b border-gray-200 pb-4">
                {t('booking.summaryTitle', 'Booking Summary')}
              </h3>

              <div className="space-y-4 mb-6">
                <div className="flex items-center">
                  <Calendar className="text-primary w-5 h-5 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">{t('booking.date', 'Date')}</p>
                    <p className="font-medium">
                      {bookingData.date && formatDate(bookingData.date)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center">
                  <Clock className="text-primary w-5 h-5 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">{t('booking.time', 'Time')}</p>
                    <p className="font-medium">{bookingData.time}</p>
                  </div>
                </div>

                <div className="flex items-center">
                  <Globe className="text-primary w-5 h-5 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">{t('booking.timezone', 'Timezone')}</p>
                    <p className="font-medium">{visitorTz}</p>
                  </div>
                </div>

                <div className="flex items-center">
                  <Timer className="text-primary w-5 h-5 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">{t('booking.duration', 'Duration')}</p>
                    <p className="font-medium">
                      {bookingData.duration} {t('booking.minutes', 'minutes')}
                    </p>
                  </div>
                </div>

                <div className="flex items-center">
                  <CreditCard className="text-primary w-5 h-5 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">{t('booking.price', 'Price')}</p>
                    <p className="font-medium">€{activePrice}</p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 p-4 rounded-xl mb-6 text-center">
                <p className="text-sm text-blue-600 font-medium">
                  {t('booking.secureStripe', 'Secure checkout via Stripe')}
                </p>
              </div>

              {serverError && (
                <div className="bg-red-50 text-red-600 p-3 rounded-xl mb-4 text-sm font-medium">
                  {serverError}
                </div>
              )}

              <div className="space-y-4 pt-4 mt-2">
                <button
                  onClick={handleStripePayment}
                  disabled={
                    !bookingData.name || !bookingData.email || !bookingData.phone || submitting
                  }
                  className="w-full flex items-center justify-center bg-gray-900 hover:bg-primary disabled:bg-gray-300 transform transition-all disabled:cursor-not-allowed text-white py-4 px-8 rounded-full shadow-md font-bold hover:shadow-lg hover:-translate-y-1"
                >
                  <CreditCard className="w-5 h-5 mr-3 hidden sm:block" />
                  {submitting
                    ? t('diets.buttons.redirecting', 'Redirecting...')
                    : `${t('booking.payWithStripe', 'Pay with Stripe')} (€${activePrice})`}
                </button>
              </div>
            </div>
          </div>

          <div className="flex justify-between mt-8 pt-6 border-t border-gray-100 relative z-10">
            <button
              onClick={prevStep}
              className="flex items-center justify-center bg-gray-50 hover:bg-gray-100 text-gray-800 py-3 px-8 rounded-full font-bold transition-all"
            >
              <ChevronLeft className="mr-2 w-4 h-4" /> {t('booking.back', 'Go Back')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImprovedBookingPage;
